/**
 * Submitting a deal from the signed-in dashboard (canon APP-009, CORE-024, decision D-008).
 *
 * Submission used to be an anonymous `POST /v1/submissions` on `dealbid-web`, keyed on an
 * email address in the body, and that endpoint created the seller row itself. `D-008`
 * withdrew it: canon § 14 makes Google sign-in the only way a founder is known, and
 * "submitting a deal requires being signed in". So a deal is now created on the collection
 * the founder already reads — `POST /v1/seller/deals`.
 *
 * Three absences carry the contract, and each is deliberate:
 *
 * **No seller, email, display name or invite code is sent.** There is nothing in this
 * request that names who is asking; the session cookie decides it. Core refuses those fields
 * rather than ignoring them, which is what stops a silently dropped invite code from leaving
 * a founder believing canon § 5's larger grant had been applied.
 *
 * **Nothing Dealbid decides is sent either** — no status, slug, Founding position or
 * verification claim. Those are core's, and a body carrying one fails loudly instead of
 * appearing to have worked.
 *
 * **The terms sentence is not written here.** Core owns the wording (CORE-016) and stamps
 * the version it holds onto the deal, so a copy in this repo could be reworded without that
 * version moving — leaving a founder recorded as having accepted a sentence nobody showed
 * them. The form reads {@link fetchSubmissionTerms} and will not send until it has it.
 */
import { apiRequest, apiRequestWithStatus } from "./client";
import { parseSellerDeal } from "./desk";
import { parseDealEditPolicy } from "./deal-edit";
import { asRecord, malformed, nonEmptyString } from "./parse";
import type { DealSubmission, SubmissionTerms, SubmittedDeal } from "./types";

/** Returns `null` when the body does not carry a usable terms sentence. */
export function parseSubmissionTerms(body: unknown): SubmissionTerms | null {
  const row = asRecord(body);
  if (row === null) return null;
  const terms = asRecord(row.terms);
  if (terms === null) return null;

  const version = nonEmptyString(terms.version);
  const text = nonEmptyString(terms.text);
  // Both halves are required. A version with no sentence is nothing to show a founder, and
  // a sentence with no version is nothing core could stamp — either way there is no
  // provable acceptance to be had, so the form must not offer one.
  if (version === null || text === null) return null;

  return { version, text };
}

/**
 * The wording a submission made now would accept — `GET /v1/submission-terms`.
 *
 * This is the seller app's **only** anonymous request, and the exception is forced by the
 * contract rather than chosen: the wording is public copy, so `contracts/API.md` § Public
 * cross-origin policy answers it without `Access-Control-Allow-Credentials`, and a browser
 * discards a credentialed response that lacks that header. Sending the session cookie here
 * would therefore not be "more secure" — it would simply make the sentence unreadable.
 */
export async function fetchSubmissionTerms(): Promise<SubmissionTerms> {
  const terms = parseSubmissionTerms(
    await apiRequest<unknown>("/v1/submission-terms", { anonymous: true }),
  );
  if (terms === null) throw malformed();
  return terms;
}

/** Returns `null` when the body is not the deal core says it created. */
export function parseSubmittedDeal(body: unknown): SubmittedDeal | null {
  const row = asRecord(body);
  if (row === null) return null;

  const deal = parseSellerDeal(row.deal);
  const editPolicy = parseDealEditPolicy(row.editPolicy);
  if (deal === null || editPolicy === null) return null;

  const terms = asRecord(row.terms);
  if (terms === null) return null;
  const version = nonEmptyString(terms.version);
  const text = nonEmptyString(terms.text);
  const acceptedAt = nonEmptyString(terms.acceptedAt);
  // The echo is required, not decorative. It is what lets the confirmation state which
  // wording was actually recorded rather than asserting that it must have been the one on
  // screen — the same discipline that makes `terms` mandatory on a credit balance.
  if (version === null || text === null || acceptedAt === null) return null;

  return { deal, editPolicy, terms: { version, text, acceptedAt } };
}

/**
 * Files a new deal for review.
 *
 * Core answers `201` with the **same seller deal projection `GET /v1/seller/deals` lists**,
 * so the desk puts the created deal straight into its panel instead of refetching to learn
 * what it just made. A body this app cannot read is reported as a failure rather than as an
 * empty success: a founder told "submitted" who then sees no deal would submit it again.
 */
export async function submitSellerDeal(submission: DealSubmission): Promise<SubmittedDeal> {
  const { body } = await apiRequestWithStatus<unknown>("/v1/seller/deals", {
    method: "POST",
    body: submission,
  });
  const created = parseSubmittedDeal(body);
  if (created === null) throw malformed();
  return created;
}
